import { LitElement, html, css } from 'lit';
import './sub-components/controls.mjs';
import './sub-components/item-display.mjs';
import '../studio-card/studio-card.mjs';
import '../studio-input/studio-input.mjs';
import '../studio-icon/studio-icon.mjs';
import '../studio-button/studio-button.mjs';
import '../studio-button-group/studio-button-group.mjs';
import '../studio-select/studio-select.mjs';
import '../studio-popup/studio-popup.mjs';
import { scrollbarStyles } from '../../styles/scrollbars.css.mjs'

export const VIEWS = {
	GRID: {
		name: "grid",
		icon: 'grip',
		label: "Grid",
		item: "studio-card variant=\"vertical\"",
		frame: "studio-gallery-grid-frame",
	},
	RIBBON: {
		name: "ribbon",
		icon: 'gallery',
		label: "Ribbon",
		item: "studio-card variant=\"vertical\"",
		frame: "studio-gallery-ribbon-frame",
	},
	LIST: {
		name: "list",
		icon: 'list',
		label: "List",
		item: "studio-card variant=\"horizontal\"",
		frame: "studio-gallery-list-frame",
	},
}

class StudioGallery extends LitElement {
	static properties = {
		// List of items
		items         		: { type: Array   },
		filteredItems 		: { type: Array   },
		itemStyle     		: { type: Object  },

		// Feature Visibility
		showSearch    		: { type: Boolean },
		showFilter    		: { type: Boolean },
		showSorter    		: { type: Boolean },
		showSwitcher  		: { type: Boolean },
		showInfo      		: { type: Boolean },

		// List of sort/filter options
		sortOptions   		: { type: Array   },
		filterOptions 		: { type: Object  },
		triggerLiveSearch : { type: Boolean },

		// View Contols
		views         		: { type: Array   },
		mode          		: { type: String  },
		viewButtons   		: { type: String  },

		// Options
		enableSearch  		: { type: Boolean },
		searchError   		: { type: String  },
		infoDisplay   		: { type: Object  },
		debounceDelay 		: { type: Number  },

		// Selection
		searchDefaults		: { type: Object  },
	};

	constructor() {
		super();
		// Initialize items
		this.items = [];
		this.filteredItems = this.items;
		this.itemStyle = null;

		// Initialize default views and set default mode
		this.views = [
			VIEWS.GRID,
			VIEWS.RIBBON,
			VIEWS.LIST
		];
		this.mode = this.views[0].name;

		// Initialize selected item
		this.currentSelection = null;

		// Initialize filter and sort options
		this.filterOptions = {};
		this.currentFilter = [];
		this.sortOptions = [];
		this.triggerLiveSearch = false;

		// Control visibility of features, all true by default
		this.showSearch		= true;
		this.showFilter		= true;
		this.showSorter		= true;
		this.showSwitcher = true;
		this.showInfo			= true;

		this.enableSearch  = true;
		this.searchError   = '';
		this.debounceDelay = 1000;

		this.searchDefaults = {};
	}

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			justify-content: center;
			height: 100%;
		}
		${scrollbarStyles}
	`;

	 
	render() {
		const view = this.views.find(v => v.name === this.mode);
		const filterList = Object.entries(this.filterOptions)
			.map(([id, value]) => ({ value: id, label: value.label }));
			
		return html`
			<studio-gallery-controls
				.views        			= ${this.views}
				.currentMode  			= ${this.mode}
				.sortOptions  			= ${this.sortOptions}
				.filterOptions			= ${filterList}
				.showSearch					= ${this.showSearch}
				.showFilter					= ${this.showFilter}
				.showSorter					= ${this.showSorter}
				.showSwitcher				= ${this.showSwitcher}
				.showInfo						= ${this.showInfo}
				.searchDefaults			= ${this.searchDefaults}
				.triggerLiveSearch 	= ${this.triggerLiveSearch}
				.enableSearch				= ${this.enableSearch}
				.errorText					= ${this.searchError}
				.infoDisplay				= ${this.infoDisplay}
				.debounceDelay			= ${this.debounceDelay}
				.viewButtons 				= ${this.viewButtons}
				@change-view				= ${this.changeView}
				@filter							= ${this.updateFilter}
				exportparts="sort-toggle"
			>
				<slot name="in-controls" slot="left"></slot>
			</studio-gallery-controls>
			<slot name="between"></slot>
			<studio-gallery-item-display
				.items							= ${this.filteredItems}
				.itemStyle					= ${this.itemStyle}
				.frame							= ${view ? view.frame :
					'studio-ui.components.gallery.grid-frame'}
				.item								= "${view ? view.item :
					'studio-ui.components.gallery.grid-item'}"
				.variant						= ${view ? view.variant : ''}
				@item-selected 			= ${this.itemSelected}
			></studio-gallery-item-display>
			<slot name="after"></slot>
		`;
	}
	 

	updated(changedProperties) {
		super.updated(changedProperties);
		if (changedProperties.has('items')) {
			this.filterItems();
		}
		if (changedProperties.has('searchDefaults')) {
			this.mode = this.searchDefaults.view;
			this.currentFilter = this.searchDefaults.filters || [];
			this.filterItems();
			this.requestUpdate();
		}
	}

	changeView(event) {
		this.mode = event.detail.mode;
		this.dispatchEvent(new CustomEvent('view-changed', {
			detail: event.detail,
			bubbles: true,
			composed: true
		}))
		this.requestUpdate();
	}

	updateFilter (event) {
		this.currentFilter = event.detail;
		this.dispatchEvent(new CustomEvent('filter-changed', {
			detail: event.detail,
			bubbles: true,
			composed: true
		}));
		this.filterItems();
	}

filterItems() {
	this.filteredItems = this.items.filter(item =>
		this.currentFilter.every(filter => {
			const option = this.filterOptions[filter];
			return typeof option?.fn === 'function'
				? option.fn(item.metadata)
				: true; // ignore invalid filters
		})
	);

	this.requestUpdate();
}


	clearItemSelection() {
		if(!this.currentSelection) return;
		this.currentSelection.selected = false;
		this.currentSelection = null;
	}

	itemSelected(event) {
		// Unselect the previously selected item
		if(this.currentSelection) {
			this.currentSelection.selected = false;
		}

		if(event.detail.element === this.currentSelection) {
			this.currentSelection = null;
		}
		else{
			this.currentSelection = event.detail.element;
		}

		// Stop event propagation
		event.stopPropagation();

		// Emit new item-selected event for parent
		this.dispatchEvent(new CustomEvent('item-selected', {
			detail: { 
				item: event.detail?.item || null,
				element: event.detail?.element || null,
			},
			bubbles: true,
			composed: true,
		}));
	}
}

customElements.define("studio-gallery", StudioGallery);
